import { useSession, useTenant } from '@zhiyue/core';
import { Alert, App, Button, Result, Spin } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';
import { ensureValidAccessToken, hasOAuth2Session } from '@/services/oauth2';
import { indexedRepository } from './persistence';
import { BusinessService, type Command } from './service';
import type { Snapshot } from './types';
export const isPrototype =
  process.env.UMI_APP_BUSINESS_DATA_MODE === 'prototype';
const Context = React.createContext<BusinessService | undefined>(undefined);
export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useSession();
  const { currentTenantUuid } = useTenant();
  const memberUuid = currentUser?.memberUuid;
  const tenantUuid = currentTenantUuid || currentUser?.tenantUuid;
  const [error, setError] = React.useState('');
  const service = React.useMemo(
    () =>
      memberUuid && tenantUuid && isPrototype
        ? new BusinessService(
            indexedRepository(
              JSON.stringify([
                BizAppConfig.appSsoClientId,
                tenantUuid,
                memberUuid,
              ]),
            ),
            {
              appId: BizAppConfig.appSsoClientId || 'srbs',
              tenantUuid,
              memberUuid,
            },
          )
        : undefined,
    [memberUuid, tenantUuid],
  );
  React.useEffect(() => {
    setError('');
    if (!service || !hasOAuth2Session()) return;
    let active = true;
    void service.load().catch((e) => {
      if (active) setError(String(e.message));
    });
    const channel =
      typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(
            `srbs:${service.identity.tenantUuid}:${service.identity.memberUuid}`,
          )
        : undefined;
    const refresh = () => {
      if (active)
        void service.refresh().catch((e) => setError(String(e.message)));
    };
    if (channel) channel.onmessage = refresh;
    const unsubscribe = service.subscribe(() =>
      channel?.postMessage('changed'),
    );
    const timer = window.setInterval(() => {
      if (hasOAuth2Session() && !document.hidden && service.hasScheduledWork())
        void service.tick().catch((e) => setError(String(e.message)));
    }, 1000);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      clearInterval(timer);
      unsubscribe();
      channel?.close();
      window.removeEventListener('focus', refresh);
    };
  }, [service]);
  if (loading) return <Spin fullscreen description="正在验证真实会话…" />;
  return (
    <Context.Provider value={service}>
      {error && (
        <Alert
          type="error"
          showIcon
          title="模拟数据保存失败"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>重新加载</Button>
          }
        />
      )}{' '}
      {children}
    </Context.Provider>
  );
}
export function useBusiness() {
  const service = React.useContext(Context);
  const state = React.useSyncExternalStore(
    service?.subscribe || (() => () => {}),
    service?.snapshot || (() => undefined),
  );
  return { service, state };
}
export function BusinessGate({
  children,
}: {
  children: (service: BusinessService, state: Snapshot) => React.ReactNode;
}) {
  const { service, state } = useBusiness();
  const { currentUser } = useSession();
  if (!isPrototype)
    return (
      <Result
        status="info"
        title="业务数据模式未配置"
        subTitle="请显式配置 UMI_APP_BUSINESS_DATA_MODE=prototype；真实业务接口尚未接入。"
      />
    );
  if (!currentUser)
    return (
      <Result
        status="403"
        title="请先登录"
        subTitle="本应用沿用真实 SSO，会话验证通过后加载模拟业务数据。"
      />
    );
  if (!service)
    return (
      <Result
        status="warning"
        title="会话缺少成员或租户上下文"
        subTitle="请通过用户菜单选择真实企业，或重新登录。"
      />
    );
  if (!state) return <Spin description="正在初始化演示数据…" />;
  return children(service, state);
}
export function useCommand() {
  const { message } = App.useApp();
  const { service } = useBusiness();
  const [busy, setBusy] = React.useState(false);
  const [commandError, setCommandError] = React.useState('');
  const pendingCommand = React.useRef(false);
  const run = async (input: Command) => {
    if (pendingCommand.current) return undefined;
    if (!service || !hasOAuth2Session()) {
      setCommandError('真实会话不可用，请重新登录。');
      message.error('真实会话不可用，请重新登录。');
      return undefined;
    }
    pendingCommand.current = true;
    setBusy(true);
    setCommandError('');
    try {
      await ensureValidAccessToken();
      const result = await service.command({
        ...input,
        meta: input.meta || { commandUuid: crypto.randomUUID() },
      });
      message.success('已保存模拟业务操作');
      return result;
    } catch (error) {
      const detail = error instanceof Error ? error.message : '保存失败';
      setCommandError(detail);
      message.error(detail);
      return undefined;
    } finally {
      pendingCommand.current = false;
      setBusy(false);
    }
  };
  return {
    run,
    busy,
    commandError,
    clearCommandError: () => setCommandError(''),
  };
}
